import React from "react";
import { useLang } from "../i18n/LanguageContext";
import { ArrowRight, ShieldCheck, Star, MapPin } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBmcmFtaW5nJTIwYnVpbGRlcnxlbnwwfHx8fDE3ODExMjAzMjZ8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { t } = useLang();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-black grain">
      {/* BG image + overlays */}
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Construction framing" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/40 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-transparent to-black/40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-32 pb-16 sm:pb-24 w-full">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 reveal">
            <div className="flex items-center gap-3 text-orange-500 font-mono-label text-xs uppercase tracking-[0.25em] mb-6">
              <span className="w-8 h-px bg-orange-500" /> {t.hero.eyebrow}
            </div>
            <h1 data-testid="hero-title" className="font-display uppercase text-white leading-[0.9] text-5xl sm:text-7xl lg:text-8xl xl:text-9xl">
              {t.hero.title1}<br />
              <span className="text-orange-500">{t.hero.title2}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-neutral-300 text-base sm:text-lg leading-relaxed">
              {t.hero.sub}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                data-testid="hero-cta"
                onClick={() => scrollTo("contact")}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-black font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(249,115,22,0.35)]"
              >
                {t.hero.ctaPrimary} <ArrowRight size={18} />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("services")}
                className="inline-flex items-center gap-2 border border-neutral-700 hover:border-orange-500 hover:text-orange-500 text-white font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-colors"
              >
                {t.hero.ctaSecondary}
              </button>
            </div>
          </div>

          <div className="lg:col-span-4 reveal" style={{ animationDelay: "0.2s" }}>
            <div className="bg-neutral-950/80 backdrop-blur-md border border-neutral-800 p-6 rounded-sm">
              <div className="flex items-center gap-3 text-neutral-400 mb-4">
                <ShieldCheck size={18} className="text-orange-500" />
                <span className="font-mono-label uppercase text-xs tracking-[0.2em]">Licensed & Insured</span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { k: "10+", v: t.hero.stat1 },
                  { k: "250+", v: t.hero.stat2 },
                  { k: "100%", v: t.hero.stat3 },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="font-display text-white text-3xl sm:text-4xl">{s.k}</div>
                    <div className="text-[10px] sm:text-xs text-neutral-500 mt-1 uppercase tracking-wide leading-tight">{s.v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-neutral-800 flex items-center gap-2 text-neutral-300 text-sm">
                <MapPin size={14} className="text-orange-500" /> Birmingham, AL
                <span className="ml-auto flex items-center gap-1 text-orange-500">
                  <Star size={14} fill="currentColor" /><Star size={14} fill="currentColor" /><Star size={14} fill="currentColor" /><Star size={14} fill="currentColor" /><Star size={14} fill="currentColor" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="relative border-y border-neutral-900 bg-black/70 backdrop-blur overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-4">
          {[...t.marquee, ...t.marquee, ...t.marquee, ...t.marquee].map((w, i) => (
            <span key={i} className="font-display uppercase text-2xl sm:text-3xl text-neutral-700 mx-8 flex items-center gap-8">
              {w}
              <span className="w-2 h-2 bg-orange-500 rounded-full" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
